/**
 * API client for replacement grant admin routes.
 *
 * POST /api/v1/admin/replacement-grants                   — multi-recipient, multi-date
 * GET  /api/v1/admin/replacement-grants?employee_id=      — per-employee list
 * POST /api/v1/admin/replacement-grants/ai-draft          — AI note + suggested days
 *
 * See docs/superpowers/specs/2026-05-18-replacement-grants-redesign-design.md
 */
import { apiRequest } from './client'

export type ReplacementGrantStatus = 'active' | 'expired' | 'fully_consumed'

export interface ReplacementGrant {
  id: string
  employeeId: string
  /** Employee name, populated by the API for list/edit/show endpoints. */
  employeeName: string | null
  /** ISO YYYY-MM-DD dates, sorted ascending. */
  workedDates: string[]
  quantity: string
  consumed: string
  remaining: string
  expiresAt: string
  notes: string | null
  status: ReplacementGrantStatus
  grantedAt: string
  grantedBy: string
  createdAt: string
  updatedAt: string
}

export interface ReplacementGrantUpdateBody {
  workedDates?: string[]
  /** Decimal as string, e.g. "2.0". */
  quantity?: string
  expiresAt?: string
  notes?: string | null
}

export interface ReplacementGrantCreateBody {
  recipientIds: string[]
  workedDates: string[]
  /** Decimal as string, e.g. "2.0". */
  quantity: string
  expiresAt?: string
  notes?: string
}

export interface AiDraftRequest {
  workedDates: string[]
  recipientIds: string[]
}

export interface AiDraftResponse {
  notes: string
  /** Decimal as string. */
  suggestedDays: string
  rationale: string
}

const BASE = '/api/v1/admin/replacement-grants'

export function createReplacementGrants(
  body: ReplacementGrantCreateBody,
): Promise<ReplacementGrant[]> {
  return apiRequest<ReplacementGrant[]>(BASE, { method: 'POST', body })
}

export function listReplacementGrants(
  employeeId: string,
): Promise<ReplacementGrant[]> {
  return apiRequest<ReplacementGrant[]>(
    `${BASE}?employee_id=${encodeURIComponent(employeeId)}`,
  )
}

/**
 * List all replacement grants across employees. HR-only on the API side.
 * Used by the HR overview at /hr/replacement-grants.
 */
export function listAllReplacementGrants(): Promise<ReplacementGrant[]> {
  return apiRequest<ReplacementGrant[]>(BASE)
}

export function cancelReplacementGrant(id: string): Promise<{ id: string }> {
  return apiRequest<{ id: string }>(`${BASE}/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  })
}

export function updateReplacementGrant(
  id: string,
  body: ReplacementGrantUpdateBody,
): Promise<ReplacementGrant> {
  return apiRequest<ReplacementGrant>(`${BASE}/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body,
  })
}
